// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { ClipboardCheck, Construction, LogOut, UserRound, UsersRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePendingActionsQuery } from "@/api/hooks/use-pending-actions";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { useDebugMode } from "@/lib/debug-mode";

function initials(fullName?: string): string {
  if (!fullName) return "?";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
}

async function sha256Hex(input: string): Promise<string> {
  const buffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function useGravatarUrl(email: string | undefined, size = 64): string | undefined {
  const [url, setUrl] = useState<string>();

  useEffect(
    function computeGravatarUrl() {
      if (!email) return;
      let cancelled = false;
      sha256Hex(email.trim().toLowerCase()).then((hash) => {
        if (!cancelled) {
          setUrl(`https://gravatar.com/avatar/${hash}?s=${size * 2}&d=404`);
        }
      });
      return () => {
        cancelled = true;
      };
    },
    [email, size],
  );

  return url;
}

export function UserDropdown() {
  const { t } = useTranslation("back-office");
  const { user, logout } = useAuth();
  const { debugMode } = useDebugMode();
  const navigate = useNavigate();
  const gravatarUrl = useGravatarUrl(user?.email);
  const { data: pendingActions } = usePendingActionsQuery();
  const approvalCount = pendingActions?.pendingCount ?? 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative inline-flex">
          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8">
            <Avatar className="h-8 w-8 border border-foreground/20">
              {gravatarUrl && <AvatarImage src={gravatarUrl} alt={user?.fullName} />}
              <AvatarFallback className="text-xs">{initials(user?.fullName)}</AvatarFallback>
            </Avatar>
          </Button>
          {debugMode && approvalCount > 0 && (
            <span className="pointer-events-none absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-white">
              {approvalCount}
            </span>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm">{user?.fullName ?? "Unknown"}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </DropdownMenuLabel>
        {debugMode && user?.groups && user.groups.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="flex items-start gap-2 font-normal">
              <UsersRound className="mt-0.5 h-4 w-4" />
              <div className="min-w-0">
                <p className="text-sm">Groups</p>
                <ul className="space-y-0.5">
                  {[...user.groups]
                    .sort((a, b) => a.localeCompare(b))
                    .map((group) => (
                      <li key={group} className="truncate text-xs text-muted-foreground">
                        – {group}
                      </li>
                    ))}
                </ul>
              </div>
            </DropdownMenuLabel>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate({ to: "/profile" })}>
          <UserRound className="h-4 w-4" />
          {t("commandPalette.myProfile")}
        </DropdownMenuItem>
        {debugMode && (
          <DropdownMenuItem
            onSelect={() => navigate({ to: "/approvals" })}
            className="text-amber-600 underline focus:text-amber-600 dark:text-amber-400 dark:focus:text-amber-400"
          >
            <ClipboardCheck className="h-4 w-4" />
            <Construction className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            {t("approvals.title")}
            {approvalCount > 0 && (
              <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-white">
                {approvalCount}
              </span>
            )}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={() => logout().finally(() => navigate({ to: "/login" }))}
        >
          <LogOut className="h-4 w-4 text-destructive" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
